import { Component, forceUpdate, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { IconLibraryThumbnail } from "./icon-library-thumbnail/icon-library-thumbnail";
import { getIconRegistry } from "../icon/registry";
import { IconLibFilters, IconLibMetaData, IconThumbnail } from "./types";
import { AVAILABLE_ICONS } from "./constants";

@Component({
    tag: "ux-icon-library",
    styleUrl: "icon-library.scss"
})
export class IconLibrary {
    private baseClass = "ux-icon-library";
    private inputRef: HTMLUxInputFieldElement | null = null;

    /** the label of the search input field */
    @Prop() readonly directSearchLabel: string = "search";
    /** the placeholder of the search input field */
    @Prop() readonly directSearchPlaceholder: string = "";
    /** the placeholder of the search input field */
    @Prop() readonly filterLabel: string = "filter";

    @State() categories: string[] = [];
    @State() assetsPath!: string;
    @State() selectedIcon!: IconThumbnail;
    @State() totalIconNumber = 0;
    @State() updateCount = true;
    @State() numberOfResults = 0;
    @State() iconsList: IconLibMetaData[] = [];
    @State() iconsToShow: IconLibMetaData[] = [];
    @State() filters: IconLibFilters = {
        directSearch: "",
        weight: [],
        type: [],
        keywords: [],
        categories: []
    };
    @State() isLoading = false;
    @State() modalIsOpened = false;
    @State() code = "";
    @State() clickedIcon = "";

    connectedCallback() {
        document.addEventListener("uxThemeChange", this.updateAssetsPath);
    }

    disconnectedCallback() {
        document.removeEventListener("uxThemeChange", this.updateAssetsPath);
    }

    componentWillLoad() {
        this.assetsPath = getIconRegistry().assetsPath;
        this.iconsList = AVAILABLE_ICONS[getIconRegistry().theme] || [];
        const categories: string[] = [];
        this.iconsList.forEach((icon: IconLibMetaData) => {
            icon.categories?.forEach((category: string) => {
                if (!categories.includes(category)) {
                    categories.push(category);
                }
            });
        });
        this.categories = categories;
        let counter = 0;
        this.iconsList.forEach((icon: IconLibMetaData) => {
            // @ts-expect-error Check for typing
            counter += icon.outlineWeights.length;
            // @ts-expect-error Check for typing
            counter += icon.filledWeights.length;
        });
        this.totalIconNumber = counter;
    }

    private updateAssetsPath = () => {
        this.isLoading = true;
        window.setTimeout(() => {
            this.assetsPath = getIconRegistry().assetsPath;
            this.iconsList = AVAILABLE_ICONS[getIconRegistry().theme] || [];
            let counter = 0;
            this.iconsList.forEach((icon: IconLibMetaData) => {
                // @ts-expect-error Check for typing
                counter += icon.outlineWeights.length;
                // @ts-expect-error Check for typing
                counter += icon.filledWeights.length;
            });
            this.totalIconNumber = counter;
            this.isLoading = false;
            // @ts-expect-error Check for typing
            this.inputRef.setAttribute("value", "");
            forceUpdate(this);
        }, 200);
    };

    @Watch("iconsList")
    updateIconsList() {
        this.iconsToShow = this.iconsList;
    }

    private handleDirectSearch = (e: CustomEvent) => {
        const eTarget = e.target as HTMLUxInputFieldElement;
        // @ts-expect-error Check for typing
        const inputValue = eTarget.querySelector("input").value;
        this.filters = {
            ...this.filters,
            directSearch: inputValue.toLowerCase()
        };
    };

    private handleWeightChange = (e: Event) => {
        const eTarget = e.target as HTMLInputElement;
        const value = eTarget.value;
        if (!eTarget.checked) {
            this.filters = {
                ...this.filters,
                weight: [...this.filters.weight.filter((size) => size !== value)]
            };
        } else {
            this.filters = {
                ...this.filters,
                weight: [...this.filters.weight, value]
            };
        }
    };

    private handleTypeChange = (e: Event) => {
        const eTarget = e.target as HTMLInputElement;
        const value = eTarget.value;
        if (!eTarget.checked) {
            this.filters = {
                ...this.filters,
                type: [...this.filters.type.filter((type) => type !== value)]
            };
        } else {
            this.filters = {
                ...this.filters,
                type: [...this.filters.type, value]
            };
        }
    };

    private handleCategoriesChange = (e: Event) => {
        const eTarget = e.target as HTMLInputElement;
        const value = eTarget.value;
        if (!eTarget.checked) {
            this.filters = {
                ...this.filters,
                categories: [...this.filters.categories.filter((cat) => cat !== value)]
            };
        } else {
            this.filters = {
                ...this.filters,
                categories: [...this.filters.categories, value]
            };
        }
    };

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    @State() legacyNameFiltered: any[] = [];

    @Watch("filters")
    filterIcons() {
        const list = this.iconsList.filter((icon: IconLibMetaData) => {
            // @ts-expect-error Check for typing
            const legacyNames = Object.keys(icon.legacyNames) || [];
            const directSearch = this.filters.directSearch;
            const lowercaseIconName = icon.name.toLowerCase();
            let legacyNamesFilter = false;
            legacyNames.forEach((name) => {
                const lowercaseName = name.toLowerCase();
                if (lowercaseName.includes(directSearch)) {
                    legacyNamesFilter = true;
                }
            });
            if (legacyNamesFilter) {
                // @ts-expect-error Check for typing
                icon["filteredLegacy"] = true;
            } else {
                // @ts-expect-error Check for typing
                icon["filteredLegacy"] = false;
            }
            const nameSearch = directSearch === "" || lowercaseIconName.includes(directSearch);
            const keywordSearch =
                directSearch === "" || icon.keywords?.find((keyword: string) => keyword.toLowerCase().includes(directSearch));
            const legacyNamesSearch = legacyNamesFilter;
            const weightSearch =
                this.filters.weight.length === 0 ||
                // @ts-expect-error Check for typing
                this.filters.weight.some((r) => icon.outlineWeights.includes(r)) ||
                // @ts-expect-error Check for typing
                this.filters.weight.some((r) => icon.filledWeights.includes(r));
            const categorySearch =
                this.filters.categories.length === 0 || this.filters.categories.some((cat) => icon.categories.includes(cat));
            return (nameSearch || keywordSearch || legacyNamesSearch) && weightSearch && categorySearch;
        });
        this.iconsToShow = list;
    }

    private onThumbnailDetailClick = (iconThumbnail: IconThumbnail) => {
        this.modalIsOpened = true;
        this.clickedIcon = iconThumbnail.iconName + iconThumbnail.iconWeight + iconThumbnail.filled;
        this.code = `<ux-icon name="${iconThumbnail.iconName}" weight="${iconThumbnail.iconWeight}"${
            iconThumbnail.filled ? ` filled` : ``
        }></ux-icon>`;
    };

    private removeFilter = ({ filter, text }: { filter: string; text: string }) => {
        this.filters = {
            ...this.filters,
            // @ts-expect-error Check for typing
            [filter]: [...this.filters[filter].filter((filter) => filter !== text)]
        };
    };

    @Listen("uxModalClose", { target: "window" })
    onModalClose() {
        this.modalIsOpened = false;
    }

    render() {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        const icons: any[] = this.isLoading
            ? []
            : this.iconsToShow.map((icon: IconLibMetaData) => {
                  const getThumbnail = (filled: boolean) => {
                      return (weight: string) => {
                          if (this.filters.weight.length !== 0 && !this.filters.weight.includes(weight)) {
                              return null;
                          }
                          if (this.filters.type.length !== 0 && !this.filters.type.includes(filled ? "filled" : "outline")) {
                              return null;
                          }
                          const key = icon.name + weight + filled;
                          // @ts-expect-error Check for typing
                          if (icon["filteredLegacy"]) {
                              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                              icon;
                          }
                          return (
                              <IconLibraryThumbnail
                                  selected={this.modalIsOpened && key === this.clickedIcon}
                                  key={key}
                                  iconName={icon.name}
                                  iconWeight={weight}
                                  filled={filled}
                                  iconDescription={icon.description}
                                  onDetailsClick={this.onThumbnailDetailClick}
                                  assetsPath={this.assetsPath}
                              />
                          );
                      };
                  };

                  // @ts-expect-error Check for typing
                  // eslint-disable-next-line @stencil-community/render-returns-host
                  return [...icon.outlineWeights.map(getThumbnail(false)), ...icon.filledWeights.map(getThumbnail(true))].filter(
                      (item) => item !== null
                  );
              });
        const iconCount = icons.reduce((a, b) => (a || 0) + b.length, 0);

        return (
            <Host class={this.baseClass}>
                <div class={`${this.baseClass}__intro-container`}>
                    <slot name="intro" />
                </div>
                <div class={`${this.baseClass}__search-container`}>
                    <ux-input-field
                        label={this.directSearchLabel}
                        placeholder={this.directSearchPlaceholder}
                        onUxInputChange={this.handleDirectSearch}
                        // @ts-expect-error Check for typing
                        ref={(el) => (this.inputRef = el)}
                        autocomplete="off"
                    ></ux-input-field>
                    <div class={`${this.baseClass}__filter-container`}>
                        <div class="ux-label">{this.filterLabel}</div>
                        <div class={`${this.baseClass}__filter`}>
                            <ux-dropdown placeholder="weight" onChange={this.handleWeightChange}>
                                {["light", "normal", "bold"].map((weight) => (
                                    <ux-checkbox-list-item
                                        key={weight}
                                        label={weight}
                                        value={weight}
                                        checked={!!this.filters.weight.find((filterWeight) => filterWeight === weight)}
                                    ></ux-checkbox-list-item>
                                ))}
                            </ux-dropdown>
                            <ux-dropdown placeholder="type" onChange={this.handleTypeChange}>
                                {["outline", "filled"].map((type) => (
                                    <ux-checkbox-list-item key={type} label={type} value={type}></ux-checkbox-list-item>
                                ))}
                            </ux-dropdown>
                            <ux-dropdown
                                search-placeholder="categories"
                                filterable
                                placeholder="categories"
                                onChange={this.handleCategoriesChange}
                            >
                                {this.categories.map((category: string) => (
                                    <ux-checkbox-list-item
                                        label={category}
                                        value={category}
                                        key={category}
                                        checked={!!this.filters.categories.find((filterCategory) => filterCategory === category)}
                                    ></ux-checkbox-list-item>
                                ))}
                            </ux-dropdown>
                        </div>
                    </div>
                </div>
                <div class={`${this.baseClass}__tags-container`}>
                    {this.filters.weight.map((weight) => {
                        return (
                            <span
                                key={weight}
                                class="ux-tag"
                                onClick={() =>
                                    this.removeFilter({
                                        filter: "weight",
                                        text: weight
                                    })
                                }
                            >
                                WEIGHT: {weight}
                                <ux-icon name="cross" weight="bold"></ux-icon>
                            </span>
                        );
                    })}
                    {this.filters.type.map((type) => {
                        return (
                            <span
                                key={type}
                                class="ux-tag"
                                onClick={() =>
                                    this.removeFilter({
                                        filter: "type",
                                        text: type
                                    })
                                }
                            >
                                TYPE: {type}
                                <ux-icon name="cross" weight="bold"></ux-icon>
                            </span>
                        );
                    })}
                    {this.filters.categories.map((category) => {
                        return (
                            <span
                                key={category}
                                class="ux-tag"
                                onClick={() =>
                                    this.removeFilter({
                                        filter: "categories",
                                        text: category
                                    })
                                }
                            >
                                CATEGORY: {category}
                                <ux-icon name="cross" weight="bold"></ux-icon>
                            </span>
                        );
                    })}
                    {this.filters.categories.length === 0 && this.filters.type.length === 0 && this.filters.weight.length === 0 && (
                        <span>No filters applied</span>
                    )}
                </div>
                {!this.isLoading && (
                    <div class={`${this.baseClass}__results-details`}>
                        <span class="ux-u-text-bold">{iconCount}</span>/{this.totalIconNumber} Results
                    </div>
                )}
                {this.isLoading && (
                    <div class={`${this.baseClass}__loading-container`}>
                        <div class="ux-horizontal-loader"></div>
                    </div>
                )}
                <ux-modal name="icon-details-modal" opened={this.modalIsOpened} sticky-position="center">
                    <ux-modal-content>
                        <div class="ux-text-title ux-u-margin-bottom-10">Code</div>
                        <ux-code language="html" copy-button="Copy" code={this.code}></ux-code>
                    </ux-modal-content>
                    <ux-modal-footer>
                        <button type="button" class="ux-button" onClick={() => (this.modalIsOpened = false)}>
                            Close
                        </button>
                    </ux-modal-footer>
                </ux-modal>
                {!this.isLoading && <div class={`${this.baseClass}__results-container`}>{icons}</div>}
            </Host>
        );
    }
}
